// src/mixins/accountLimits.js
// Frontend side of plan limits: usage counters + effective limits + upgrade hint.
// Fail-open: while planUsage is null (loading / error / no permission), never block —
// the DB triggers (002_plans_and_limits.sql) are the safety net.
import GetGroupPlanUsage from '@/graphQlQueries/queries/getGroupPlanUsage';
import GetPagesCountOfProject from '@/graphQlQueries/queries/getPagesCountOfProject';
import {
  effectiveLimits,
  canCreate,
  parseLimitExceededError,
} from '@/utils/accountLimits';

export default {
  data() {
    return {
      planUsage: null,
    };
  },
  apollo: {
    planUsage: {
      query: GetGroupPlanUsage,
      fetchPolicy: 'network-only',
      variables() {
        return { groupId: this.limitsGroupId };
      },
      skip() {
        return !this.limitsGroupId;
      },
      update(data) {
        if (!data.groups_by_pk) return null;
        return {
          plan: data.groups_by_pk.plan,
          limitsOverride: data.groups_by_pk.limits_override,
          projectCount: data.projects_aggregate.aggregate.count,
          userCount: data.users_roles_groups_aggregate.aggregate.count,
        };
      },
      error(error) {
        console.error('planUsage query failed (fail-open):', error);
        return false;
      },
    },
  },
  computed: {
    // Which group the limits apply to. Components may override this
    // (e.g. when acting on a group other than the session group).
    limitsGroupId() {
      return this.groupId;
    },
    accountLimits() {
      if (!this.planUsage) {
        return { limit_projects: null, limit_pages_per_project: null, limit_users: null };
      }
      return effectiveLimits(this.planUsage.plan, this.planUsage.limitsOverride);
    },
    canCreateProject() {
      if (!this.planUsage) return true;
      return canCreate(this.planUsage.projectCount, this.accountLimits.limit_projects);
    },
    canInviteUser() {
      if (!this.planUsage) return true;
      return canCreate(this.planUsage.userCount, this.accountLimits.limit_users);
    },
  },
  methods: {
    async canCreatePageInProject(projectId) {
      const limit = this.accountLimits.limit_pages_per_project;
      if (limit === null || !projectId) return true;
      try {
        const { data } = await this.$apollo.query({
          query: GetPagesCountOfProject,
          variables: { projectId },
          fetchPolicy: 'network-only',
        });
        return canCreate(data.pages_aggregate.aggregate.count, limit);
      } catch (error) {
        console.error('pages count query failed (fail-open):', error);
        return true;
      }
    },
    showLimitReachedToast(kind) {
      this.$bvToast.toast(
        this.$t(`planLimits.${kind}LimitReached`),
        {
          title: this.$t('planLimits.limitReachedTitle'),
          variant: 'warning',
          solid: true,
          autoHideDelay: 8000,
        },
      );
    },
    handleLimitError(error) {
      const kind = parseLimitExceededError(error);
      if (!kind) return false;
      this.showLimitReachedToast(kind);
      return true;
    },
    refetchPlanUsage() {
      if (this.$apollo.queries.planUsage) {
        this.$apollo.queries.planUsage.refetch();
      }
    },
  },
};
